import { CalendarDays, Package, PawPrint, Pill, RefreshCcw, ScrollText } from 'lucide-react'
import { useState } from 'react'

import { formatInstantDate, formatQuantity, pluralizeUnit } from '@snootsvet/shared'
import type { PublicOrder, PublicOrderStatus, RegionId } from '@snootsvet/shared/portal'
import { resolveDateFormat } from '@snootsvet/shared/portal'

import { ActionCard } from '@/customer/components/action-card'
import { ClinicMapsLink } from '@/customer/components/clinic-maps-link'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { RefillOrderDialog } from '@/customer/components/orders/refill-order-dialog'
import { PageContainer } from '@/customer/components/page-container'
import { Pagination } from '@/customer/components/pagination'
import type { StackedHeadlineItem } from '@/customer/components/stacked-headline'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/customer/components/ui/tabs'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useListOrderRefills } from '@/customer/hooks/use-list-order-refills'
import { useListOrders } from '@/customer/hooks/use-list-orders'
import { useRegion } from '@/customer/hooks/use-region'

const HEADLINE: StackedHeadlineItem[] = [
  { text: 'Your', color: 'yellow' },
  { text: 'orders', color: 'pink' },
]

const PAGE_SIZE = 5
const CURRENT_ORDER_STATUSES: PublicOrderStatus[] = ['pending', 'out_of_stock']
const PAST_ORDER_STATUSES: PublicOrderStatus[] = ['collected']
const PAGE_DESCRIPTION = 'View and manage your medication orders.'

/** Status chip styling per order status. */
const STATUS_STYLES: Record<PublicOrder['status'], { label: string; className: string }> = {
  pending: { label: 'Awaiting preparation', className: 'bg-yellow' },
  prepared: { label: 'Ready for collection', className: 'bg-green' },
  collected: { label: 'Collected', className: 'bg-neutral-300' },
  out_of_stock: { label: 'Out of stock', className: 'bg-warning' },
}

/** Compact count badge for an orders tab. */
function TabCountBadge({ count }: { count: number }) {
  return (
    <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-foreground px-1 text-xs font-bold text-background">
      {count}
    </span>
  )
}

function OrderCard({ order, regionId }: { order: PublicOrder; regionId: RegionId }) {
  const style = STATUS_STYLES[order.status]
  const quantityLabel = `${formatQuantity(order.quantity)} ${pluralizeUnit(order.unit, order.quantity)}`

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border-2 border-foreground">
      <div className={`flex items-center justify-between px-4 py-2.5 ${style.className}`}>
        <p className="text-button-sm">{style.label}</p>
        <span className="flex items-center gap-1 text-button-sm">
          <PawPrint className="size-3.5" />
          {order.petName}
        </span>
      </div>
      <div className="flex flex-col gap-1.5 bg-muted p-4">
        <p className="text-button">{order.productName}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-label-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CalendarDays className="size-3.5" aria-hidden />
            {formatInstantDate(order.createdAt, resolveDateFormat(null, regionId))}
          </span>
          <span className="flex items-center gap-1.5">
            <Pill className="size-3.5" aria-hidden />
            {quantityLabel}
          </span>
          {order.treatment?.instructions && (
            <span className="flex items-center gap-1.5">
              <ScrollText className="size-3.5" aria-hidden />
              {order.treatment.instructions}
            </span>
          )}
        </div>
        <p className="mt-1 text-button-sm">{order.clinic.name}</p>
        <ClinicMapsLink clinic={order.clinic} />
      </div>
    </div>
  )
}

/**
 * Member-scoped orders page. Ready-for-collection orders stay visible;
 * pending and out-of-stock orders appear in the in-progress section;
 * collected orders appear in server-paginated history.
 */
export function OrdersPage() {
  useDocumentTitle('Your orders')
  const region = useRegion()
  const [historyPage, setHistoryPage] = useState(1)
  const [refillOpen, setRefillOpen] = useState(false)
  const refillsQuery = useListOrderRefills(true)
  const activeOrders = useListOrders({
    status: ['prepared', ...CURRENT_ORDER_STATUSES],
    limit: 50,
  })
  const pastOrders = useListOrders(
    {
      status: PAST_ORDER_STATUSES,
      limit: PAGE_SIZE,
      offset: (historyPage - 1) * PAGE_SIZE,
    },
    { keepPreviousData: true }
  )

  if (activeOrders.isPending || pastOrders.isPending) {
    return (
      <PageContainer headline={HEADLINE} description={PAGE_DESCRIPTION}>
        <LoadingSpinner />
      </PageContainer>
    )
  }

  const active = activeOrders.data?.orders ?? []
  const ready = active.filter((order) => order.status === 'prepared')
  const inProgress = active.filter((order) => order.status !== 'prepared')
  const past = pastOrders.data?.orders ?? []
  const pastTotal = pastOrders.data?.total ?? 0
  const totalPages = Math.ceil(pastTotal / PAGE_SIZE)

  return (
    <PageContainer headline={HEADLINE} description={PAGE_DESCRIPTION}>
      {refillsQuery.data?.refills.length ? (
        <div className="mb-8">
          <ActionCard
            icon={RefreshCcw}
            label="Re-order medication"
            tone="yellow"
            variant="solid"
            onClick={() => setRefillOpen(true)}
          />
        </div>
      ) : null}
      {active.length === 0 && pastTotal === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-12">
          <Package className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No orders yet.</p>
        </div>
      ) : (
        <Tabs defaultValue={active.length > 0 ? 'current' : 'past'} className="gap-6">
          <TabsList className="min-h-11 w-full">
            <TabsTrigger
              value="current"
              className="min-w-0 whitespace-normal text-button-sm data-active:bg-primary"
            >
              <span className="min-w-0 text-center text-xs">Active Orders</span>
              <TabCountBadge count={active.length} />
            </TabsTrigger>
            <TabsTrigger
              value="past"
              className="min-w-0 whitespace-normal text-button-sm data-active:bg-primary"
            >
              <span className="min-w-0 text-center text-xs">Past Orders</span>
              <TabCountBadge count={pastTotal} />
            </TabsTrigger>
          </TabsList>
          <TabsContent value="current">
            {active.length === 0 ? (
              <p className="text-body-md text-muted-foreground">No current orders.</p>
            ) : (
              <div className="flex flex-col gap-8">
                {ready.length > 0 && (
                  <section className="flex flex-col gap-4">
                    <h2 className="text-button">Ready for collection ({ready.length})</h2>
                    <div className="flex flex-col gap-4">
                      {ready.map((order) => (
                        <OrderCard key={order.id} order={order} regionId={region.id} />
                      ))}
                    </div>
                  </section>
                )}
                {inProgress.length > 0 && (
                  <section className="flex flex-col gap-4">
                    <h2 className="text-button">In progress ({inProgress.length})</h2>
                    <div className="flex flex-col gap-4">
                      {inProgress.map((order) => (
                        <OrderCard key={order.id} order={order} regionId={region.id} />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </TabsContent>
          <TabsContent value="past">
            {pastTotal === 0 ? (
              <p className="text-body-md text-muted-foreground">No past orders.</p>
            ) : (
              <div className="flex flex-col gap-4">
                {past.map((order) => (
                  <OrderCard key={order.id} order={order} regionId={region.id} />
                ))}
                <Pagination
                  page={historyPage}
                  totalPages={totalPages}
                  onPageChange={setHistoryPage}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
      <RefillOrderDialog open={refillOpen} onOpenChange={setRefillOpen} />
    </PageContainer>
  )
}
